/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Box } from "@material-ui/core";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";

import { LiveCommerceItemCounter } from "./LiveCommerceItemCounter";

import type { ProductData } from "@spwn/types/firebase/firestore";
import type { EventInfo } from "utility";

type Props = {
  THEOPlayerElementHeight: number;
  isDarkModeEnabled: boolean;
  pseudoCart: (ProductData & { count: number })[];
  displayEvent: EventInfo;
  removeCartItem?: (itemData: ProductData) => void;
  incrementCartItem: (itemData: ProductData) => void;
  decrementCartItem: (itemData: ProductData) => void;
};

export const StreamingLiveCommerceItemListV2: React.FC<Props> = (props) => {
  const { t } = useI18n();
  const classesLiveCommerceCartItemListDesign = liveCommerceCartItemListDesign({
    THEOPlayerElementHeight: props.THEOPlayerElementHeight,
    isDarkModeEnabled: props.isDarkModeEnabled,
  });
  const ticketsToBePurchasedList = props.pseudoCart.filter(
    (item) => item.productType === "ticket"
  );
  const goodsToBePurchasedList = props.pseudoCart.filter(
    (item) => item.productType === "goods"
  );

  const { removeCartItem } = props;

  return (
    <React.Fragment>
      {props.pseudoCart.length === 0 ? (
        <div css={classesLiveCommerceCartItemListDesign.yourCartIsEmpty}>
          <p>{t("streaming.commerceCart.emptyCart")}</p>
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <p
            css={css`
              font-size: 14px;
              font-weight: 700;
            `}
          >
            {t("streaming.commerceCart.cart")}
          </p>
          {ticketsToBePurchasedList.length > 0 && (
            <React.Fragment>
              {/* <div css={classesLiveCommerceCartItemListDesign.categoryHeader}>
                            <p>TICKET</p>
                          </div> */}
              <div css={classesLiveCommerceCartItemListDesign.item}>
                <div css={classesLiveCommerceCartItemListDesign.headerImage}>
                  <img
                    src={props.displayEvent.thumbnail}
                    alt={props.displayEvent.title}
                  />
                </div>
                <div css={classesLiveCommerceCartItemListDesign.headerText}>
                  <h3>{props.displayEvent.title}</h3>
                </div>
              </div>
              {ticketsToBePurchasedList.map((ticket, index) => {
                return (
                  <div
                    css={classesLiveCommerceCartItemListDesign.item}
                    key={index}
                  >
                    <div css={classesLiveCommerceCartItemListDesign.ticketText}>
                      <p css={classesLiveCommerceCartItemListDesign.goodsName}>
                        {ticket.name}
                      </p>
                      <p css={classesLiveCommerceCartItemListDesign.price}>
                        ¥{ticket.price_jpy.toLocaleString()}
                      </p>
                    </div>
                    {/* <div css={classesLiveCommerceCartItemListDesign.operation}> */}
                    {/* <div css={classesLiveCommerceCartItemListDesign.selectNumButton}>
                                      <div onClick={ () => decrementNum(ticket) }><RemoveIcon/></div>
                                      <p>{ticket.count}</p>
                                      <div onClick={ () => incrementNum(ticket) }><AddIcon/></div>
                                    </div> */}
                    {removeCartItem && (
                      <p
                        css={classesLiveCommerceCartItemListDesign.remove}
                        onClick={() => removeCartItem(ticket)}
                      >
                        {t("streaming.commerceCart.remove")}
                      </p>
                    )}
                    {/* </div> */}
                  </div>
                );
              })}
            </React.Fragment>
          )}

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              padding: 0 16px 16px;
              border-bottom: 1px solid;
              border-color: ${props.isDarkModeEnabled
                ? "rgba(255, 255, 255, 0.2)"
                : "#e0e0e0"};
            `}
          >
            {goodsToBePurchasedList.length > 0 &&
              goodsToBePurchasedList.map((cartItem) => {
                return (
                  <div
                    key={cartItem._id}
                    css={classesLiveCommerceCartItemListDesign.item}
                  >
                    <div css={classesLiveCommerceCartItemListDesign.image}>
                      <img src={cartItem.thumbnail} alt={cartItem.name} />
                    </div>
                    <div>
                      <p css={classesLiveCommerceCartItemListDesign.goodsName}>
                        {cartItem.name}
                      </p>
                      <p css={classesLiveCommerceCartItemListDesign.size}>
                        {cartItem.subClassName}
                      </p>
                      <p css={classesLiveCommerceCartItemListDesign.price}>
                        ¥{cartItem.price_jpy.toLocaleString()}
                      </p>
                      <Box marginTop={"8px"}>
                        <LiveCommerceItemCounter
                          count={cartItem.count}
                          decrementNumber={() =>
                            props.decrementCartItem(cartItem)
                          }
                          incrementNumber={() =>
                            props.incrementCartItem(cartItem)
                          }
                        />
                      </Box>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

type LiveCommerceCartDesignProps = {
  THEOPlayerElementHeight: number;
  isDarkModeEnabled: boolean;
};

const liveCommerceCartItemListDesign = (props: LiveCommerceCartDesignProps) => {
  const textColorInDarkMode = props.isDarkModeEnabled
    ? css`
        color: #fff;
      `
    : null;
  // ダークモードがオフの時だけボーダーを表示
  // const categoryHeader = css`
  //   padding: 8px 0;
  //   border-bottom: 1px solid #e0e0e0;
  //   background-color: #f4f4f4;
  //   p{
  //     padding: 0 12px;
  //     font-size: 15px;
  //     font-weight: bold;
  //   }
  // `
  const headerImage = css`
    width: 150px;
    margin-right: 10px;
    img {
      width: 100%;
    }
  `;
  const headerText = css`
    width: calc(100% - 160px);
    h3 {
      margin-bottom: 16px;
      ${textColorInDarkMode}
      font-size: 16px;
      font-weight: normal;
      line-height: 1.3em;
    }
  `;
  const item = css`
    display: flex;
  `;
  const image = css`
    min-width: 90px;
    max-width: 90px;
    height: auto;
    margin-right: 16px;
    img {
      width: 100%;
    }
  `;
  const ticketText = css`
    width: calc(100% - 80px);
  `;
  const goodsName = css`
    margin-bottom: 10px;
    ${textColorInDarkMode}
    font-size: 14px;
    line-height: 1.3em;
    word-break: break-all;
  `;
  const size = css`
    margin-bottom: 10px;
    color: #b1b1b1;
    font-size: 14px;
  `;
  const price = css`
    ${textColorInDarkMode}
  `;
  const operation = css`
    width: 70px;
    margin-left: 10px;
  `;
  const selectNumButton = css`
    display: flex;
    width: 100%;
    margin-bottom: 20px;
    border: 1px solid #e5e5e5;
    border-radius: 3px;
    input,
    div,
    p {
      display: flex;
      align-items: center;
      justify-content: center;
      width: calc(100% / 3);
    }
    input,
    div {
      padding: 4px 7px;
    }
    input,
    p {
      font-size: 14px;
      text-align: center;
      background-color: #fff;
    }
    div {
      border-left: 1px solid #e5e5e5;
      background-color: #f4f4f4;
      cursor: pointer;
      svg {
        width: 15px;
        height: 15px;
      }
    }
  `;
  const remove = css`
    color: #b1b1b1;
    font-size: 12px;
    text-align: right;
    cursor: pointer;
  `;
  const yourCartIsEmpty = css`
    padding: 20px 0;
    p {
      ${textColorInDarkMode}
      font-size: 14px;
      text-align: center;
    }
  `;
  return {
    // categoryHeader,
    headerImage,
    headerText,
    item,
    image,
    ticketText,
    goodsName,
    size,
    price,
    operation,
    selectNumButton,
    remove,
    yourCartIsEmpty,
  };
};
